"use client"

import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"
import type { DisplayTextEmbed, DisplayTextInteraction } from "../../shared/types"

// ============================================================================
// Palette Variants (mirrors SelectTrigger pattern)
// ============================================================================

const textEntryVariants = cva(
	[
		// Base styles matching Select trigger
		"inline-flex items-center justify-center",
		"h-7 px-2 rounded-xs text-sm font-semibold",
		"bg-[var(--text-entry-background)] text-[var(--text-entry-foreground)]",
		"border border-[var(--text-entry-complement)]", // border = 1px width, border-[...] = color
		"shadow-[0_3px_0_var(--text-entry-complement)]",
		"transition-all duration-150",
		"hover:brightness-90 dark:hover:brightness-75",
		// Placeholder and focus (no ring - matches Select component)
		"placeholder:text-muted-foreground/60",
		"focus:outline-none",
		// Disabled state
		"disabled:cursor-not-allowed disabled:opacity-50",
		// Invalid state
		"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
	],
	{
		variants: {
			palette: {
				default: [
					"[--text-entry-background:var(--color-background)]",
					"[--text-entry-foreground:var(--color-foreground)]",
					"[--text-entry-complement:var(--color-accent)]"
				],
				betta: [
					"[--text-entry-background:var(--color-background)]",
					"[--text-entry-foreground:var(--color-betta)]",
					"[--text-entry-complement:var(--color-butterfly)]"
				],
				cardinal: [
					"[--text-entry-background:hsl(var(--cardinal)/0.15)]",
					"[--text-entry-foreground:var(--color-cardinal)]",
					"[--text-entry-complement:var(--color-fire-ant)]"
				],
				bee: [
					"[--text-entry-background:var(--color-background)]",
					"[--text-entry-foreground:var(--color-bee)]",
					"[--text-entry-complement:var(--color-lion)]"
				],
				owl: [
					"[--text-entry-background:hsl(var(--owl)/0.15)]",
					"[--text-entry-foreground:var(--color-owl)]",
					"[--text-entry-complement:var(--color-tree-frog)]"
				],
				macaw: [
					"[--text-entry-background:var(--color-background)]",
					"[--text-entry-foreground:var(--color-macaw)]",
					"[--text-entry-complement:var(--color-whale)]"
				]
			}
		},
		defaultVariants: {
			palette: "default"
		}
	}
)

export type TextEntryPalette = NonNullable<VariantProps<typeof textEntryVariants>["palette"]>

// ============================================================================
// Types
// ============================================================================

export interface TextEntryInteractionProps {
	/** Text interaction payload (block-level) */
	interaction: DisplayTextInteraction
	/** Current user response */
	response?: string
	/** Callback when user types */
	onAnswerSelect?: (value: string) => void
	/** Whether to disable the interaction */
	disabled?: boolean
	/** Whether submit occurred; used to lock inputs and show feedback */
	hasSubmitted?: boolean
	/** Color palette for the input */
	palette?: TextEntryPalette
	/** Overall correctness for the entered value (used to switch palette after submit) */
	isCorrect?: boolean
}

export interface InlineTextEntryProps {
	/** Text entry embed payload (inline within rich stimulus) */
	embed: DisplayTextEmbed
	/** Current user response */
	response?: string
	/** Callback when user types */
	onAnswerSelect?: (value: string) => void
	/** Whether to disable the interaction */
	disabled?: boolean
	/** Whether submit occurred */
	hasSubmitted?: boolean
	/** Color palette for the input */
	palette?: TextEntryPalette
	/** Overall correctness (used to switch palette after submit) */
	isCorrect?: boolean
}

// ============================================================================
// Block-level Text Entry Renderer
// ============================================================================

/**
 * Renders a block-level text entry interaction.
 * Used when text entry is a standalone interaction block (rare).
 */
export function TextEntryInteraction({
	interaction,
	response,
	onAnswerSelect,
	disabled = false,
	hasSubmitted,
	palette = "macaw",
	isCorrect
}: TextEntryInteractionProps) {
	const value = response ?? ""
	const hasValue = value.length > 0

	// Determine palette based on state:
	// - Empty → default (grey/neutral)
	// - Has value, not submitted → palette prop (macaw)
	// - Submitted correct → owl (green)
	// - Submitted incorrect → cardinal (red)
	const getActivePalette = (): TextEntryPalette => {
		if (hasSubmitted && hasValue) {
			return isCorrect ? "owl" : "cardinal"
		}
		if (hasValue) {
			return palette
		}
		return "default"
	}
	const activePalette = getActivePalette()

	// Width based on expected length with sensible defaults
	// Add 4ch buffer to account for px-2 padding (approx 2ch) and borders
	const charWidth = interaction.expectedLength ?? 10
	const inputWidth = `${Math.max(charWidth, 3) + 4}ch`

	return (
		<div className="my-4 inline-block">
			<input
				type="text"
				value={value}
				onChange={(e) => onAnswerSelect?.(e.target.value)}
				disabled={disabled || hasSubmitted}
				placeholder={interaction.placeholder ?? "..."}
				pattern={interaction.patternMask}
				className={cn(textEntryVariants({ palette: activePalette }))}
				style={{ width: inputWidth, minWidth: "8ch" }}
				aria-invalid={hasSubmitted && hasValue && isCorrect === false ? true : undefined}
			/>
		</div>
	)
}

// ============================================================================
// Inline Text Entry Renderer
// ============================================================================

/**
 * Renders an inline text entry interaction (embedded within rich stimulus).
 * Matches the compact styling of InlineInteraction (Select) for consistency.
 */
export function InlineTextEntry({
	embed,
	response,
	onAnswerSelect,
	disabled = false,
	hasSubmitted,
	palette = "macaw",
	isCorrect
}: InlineTextEntryProps) {
	const value = response ?? ""
	const hasValue = value.length > 0

	// Determine palette based on state:
	// - Empty → default (grey/neutral)
	// - Has value, not submitted → palette prop (macaw)
	// - Submitted correct → owl (green)
	// - Submitted incorrect → cardinal (red)
	const getActivePalette = (): TextEntryPalette => {
		if (hasSubmitted && hasValue) {
			return isCorrect ? "owl" : "cardinal"
		}
		if (hasValue) {
			return palette
		}
		return "default"
	}
	const activePalette = getActivePalette()

	// Width based on expected length - keep it tight for inline use
	// Add 3ch buffer to account for px-2 padding and visual balance
	const charWidth = embed.expectedLength ?? 4
	const inputWidth = `${Math.max(charWidth, 2) + 3}ch`

	return (
		<input
			type="text"
			value={value}
			onChange={(e) => onAnswerSelect?.(e.target.value)}
			disabled={disabled || hasSubmitted}
			placeholder="..."
			pattern={embed.patternMask}
			className={cn(textEntryVariants({ palette: activePalette }), "align-baseline")}
			style={{ width: inputWidth, minWidth: "6ch" }}
			aria-invalid={hasSubmitted && hasValue && isCorrect === false ? true : undefined}
		/>
	)
}

export default TextEntryInteraction
